/**
 * External dependencies.
 */
import type { GetStaticProps, GetStaticPaths } from "next";
import Breadcrumbs from 'blocks/breadcrumbs/breadcrumbs';
import FormTopicSearch from 'blocks/form-topic-search/form-topic-search';
import HreflangControlled from 'blocks/hreflang/hreflang-controlled';
import SideContent from 'blocks/side-content/side-content';
import SchemaSet from 'components/schema-set/schema-set';
import SEO from 'components/seo/seo';
import TealBox from 'components/teal-box/teal-box';
import useFaqsThemeSlug from 'hooks/use-faqs-theme-slug';
import Layout from 'layout/layout';
/**
 * Internal dependencies.
 */
import { fetchAPI, getLocale, getLocalizationData, getSlug, loadTranslation } from 'utils';

import { t } from '@lingui/macro';

const FAQs = ({ data, current, globalData, locale, helpData, app }: any) => {
  const hreflangs = useFaqsThemeSlug(current, false);

  return (
    <Layout title="FAQs" desc="FAQs" light notice hreflang={false}>
      <HreflangControlled localizations={hreflangs} />
      <SEO data={current.attributes.SEO} />
      <SchemaSet
        schemaData={{
          "@context": "https://schema.org",
          "@type": "Question",
          name: current.attributes.Title,
          acceptedAnswer: {
            "@type": "Answer",
            text: current.attributes.Content,
          },
        }}
      />
      <TealBox center maxWidth="544px">
        <h1 className="h3 ta-c">{helpData.attributes.title}</h1>
        <FormTopicSearch locale={locale} />
      </TealBox>
      <div className="container container--small">
        <Breadcrumbs
          omitIndexList={[3, 4]}
          transformLabel={(label) => (label === "faqs" ? "FAQs" : label)}
        />
      </div>
      <SideContent
        data={data}
        current={current}
        global={globalData}
        title={t`FAQs`}
        app={app}
      />
    </Layout>
  );
};

export const getStaticProps: GetStaticProps = async (ctx) => {
  const translation = await loadTranslation(
    ctx.locale!,
    process.env.NODE_ENV === "production"
  );
  const locale = getLocale(ctx.locale);

  const { data: globalData } = await fetchAPI("/global", {
    populate: "*",
    locale,
  });

  const { data: helpData } = await fetchAPI("/help-centre-landing", {
    populate: "*",
    locale,
  });

  const { data } = await fetchAPI(`/faqs`, {
    populate: {
      SEO: {
        populate: "*",
      },
      themes: "*",
      Title: "*",
      Content: "*",
      slug: "*",
      Useful: "*",
      NotUseful: "*",
      order: "*",
      localizations: {
        populate: "*",
      },
    },
    locale,
    filters: {
      themes: {
        $or: [
          {
            slug_overwrite: ctx.params?.theme,
          },
          {
            slug: ctx.params?.theme,
          },
        ],
      },
    },
    sort: "order",
    pagination: {
      pageSize: 9999
    },
  });

  const current = data.find(
    (item: any) =>
      item.attributes.slug_overwrite == ctx.params?.slug ||
      item.attributes.slug == ctx.params?.slug
  );

  return {
    props: {
      translation,
      data: data,
      app: false,
      current,
      helpData,
      globalData,
      locale: ctx.locale,
      localizationData: getLocalizationData(current, "", "themes"),
    },
    revalidate: 10,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  let paths: any = [];
  const { data } = await fetchAPI("/faqs", {
    populate: {
      themes: "*",
      localizations: {
        populate: {
          themes: "*",
        },
      },
    },
    pagination: {
      pageSize: 9999
    },
  });

  
  data?.map(({ attributes }: any) => {
    if (attributes.localizations?.data.length) {
      attributes.localizations?.data.map((item: any) => {
        if (item.attributes.themes?.data) {
          item.attributes.themes?.data.map((theme: any) => {
            paths.push({
              params: {
                theme: getSlug(theme.attributes),
                slug: getSlug(item.attributes),
              },
              locale: item.attributes.locale.toLowerCase(),
            });
          });
        }
      });
    }

    attributes.themes?.data?.map((theme: any) => {
      paths.push({
        params: {
          theme: getSlug(theme.attributes),
          slug: getSlug(attributes),
        },
        locale: attributes.locale.toLowerCase(),
      });
    });
  });

  return {
    paths,
    fallback: false,
  };
};

export default FAQs;
